import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChefHat, RefreshCw, X } from 'lucide-react';
import apiService from '../../services/api';
import PosModalShell from '../caisse/PosModalShell';
import { createCaisseIdempotencyKey } from '../../utils/caisseIdempotency';
import {
  formatPrepNothingToSendToast,
  formatPrepSendSuccessToast,
} from '../../utils/restaurantPrepSendFeedback';
import {
  buildPrepIntentsFromSelection,
  countAllPendingKitchenLines,
  countSelectedPendingKitchenLines,
  ticketLineNeedsKitchenSend,
  ticketLinePrepActive,
} from '../../utils/restaurantPrepPanier';

const PREP_STATUS_LABELS = {
  TO_PREPARE: 'À préparer',
  IN_PREP: 'En préparation',
  READY: 'Prêt',
  SERVED: 'Servi en salle',
  CANCELLED: 'Annulé',
};

function formatMoney(v) {
  if (v == null || v === '') return '0,00 €';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)} €`;
}

function formatTime(iso) {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
  } catch {
    return null;
  }
}

function lineOptionsText(ligne) {
  const raw = ligne?.options ?? ligne?.optionsPayload ?? ligne?.choixOptions;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map((o) => o?.nom || o?.label || JSON.stringify(o)).join(', ');
      }
    } catch {
      return raw;
    }
  }
  if (Array.isArray(raw)) {
    return raw.map((o) => o?.nom || o?.label || '').filter(Boolean).join(', ');
  }
  return null;
}

function paymentStatusLabel(ticket, solde) {
  if (ticket?.statut === 'ANNULEE') return 'Addition annulée';
  if (Number.isFinite(solde) && solde <= 0.009) return 'Addition réglée';
  const enc = Number(ticket?.montantEncaisse || 0);
  if (enc > 0.009) return 'Partiellement réglée';
  return 'En attente de règlement';
}

function productNeedsPrep(line) {
  if (line?.requiresPreparation != null) return Boolean(line.requiresPreparation);
  if (line?.produit?.requiresPreparation != null) return Boolean(line.produit.requiresPreparation);
  return null;
}

function operationalLabel(line, prepLine, selectedForSend) {
  if (prepLine && ticketLinePrepActive(prepLine)) {
    return PREP_STATUS_LABELS[prepLine.status] || prepLine.status;
  }
  const needsPrep = productNeedsPrep(line);
  if (needsPrep === false) return 'Sans envoi cuisine';
  if (needsPrep === true) {
    const stationId = line?.preparationStationId ?? line?.produit?.preparationStationId;
    if (!stationId) return 'Préparation requise — poste non configuré';
    if (selectedForSend) return 'Sélectionné pour envoi';
    return 'À suivre';
  }
  return 'Préparation — information indisponible';
}

function initialSelectedIds(produits, prepById) {
  const set = new Set();
  for (const l of produits || []) {
    const prep = prepById.get(l.id);
    if (!ticketLineNeedsKitchenSend(l, prep)) continue;
    if (l.prepReleaseIntent !== 'DEFER') set.add(l.id);
  }
  return set;
}

function prepMapFromTicketProduits(produits) {
  const map = new Map();
  for (const l of produits || []) {
    if (l?.posPrepLine) map.set(l.id, l.posPrepLine);
  }
  return map;
}

/** IDs à envoyer en SELECTION après persistance NOW/DEFER (contrat métier). */
function commandeProduitIdsForSelectionSend(freshProduits, externalPrepById) {
  const ids = [];
  for (const l of freshProduits || []) {
    const prep = externalPrepById.get(l.id) || l.posPrepLine;
    if (!ticketLineNeedsKitchenSend(l, prep)) continue;
    if (l.prepReleaseIntent === 'DEFER') continue;
    ids.push(l.id);
  }
  return ids;
}

export default function RestaurantAdditionDetailModal({
  open,
  additionId,
  commercantId,
  showPrepTracking = false,
  onClose,
  onResumeAddition,
  onPrepSent,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [ticket, setTicket] = useState(null);
  const [prepLines, setPrepLines] = useState([]);
  const [markingId, setMarkingId] = useState(null);
  const [sending, setSending] = useState(false);
  const [intentSyncing, setIntentSyncing] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const selectionTouchedRef = useRef(false);

  const reload = useCallback(async () => {
    if (!open || !additionId || !commercantId) return;
    setLoading(true);
    setError('');
    setToast('');
    try {
      const detail = await apiService.getTicketDetail(additionId, commercantId);
      const t = detail?.ticket || null;
      setTicket(t);
      if (showPrepTracking) {
        const prep = await apiService.getRestaurantCommandePrepLines(additionId, commercantId);
        setPrepLines(prep?.lines || []);
      } else {
        setPrepLines([]);
      }
    } catch (e) {
      setError(e.message || 'Impossible de charger la commande');
      setTicket(null);
      setPrepLines([]);
    } finally {
      setLoading(false);
    }
  }, [open, additionId, commercantId, showPrepTracking]);

  useEffect(() => {
    reload();
  }, [reload]);

  const prepByCommandeProduitId = useMemo(() => {
    const map = new Map();
    for (const pl of prepLines) {
      if (pl.commandeProduitId) map.set(pl.commandeProduitId, pl);
    }
    for (const l of ticket?.produits || []) {
      if (l.posPrepLine?.id && !map.has(l.id)) {
        map.set(l.id, l.posPrepLine);
      }
    }
    return map;
  }, [prepLines, ticket?.produits]);

  useEffect(() => {
    if (!open) {
      selectionTouchedRef.current = false;
      setSelectedIds(new Set());
    }
  }, [open, additionId]);

  useEffect(() => {
    if (!open || !ticket?.produits?.length) return;
    if (selectionTouchedRef.current) return;
    const prepMap = prepMapFromTicketProduits(ticket.produits);
    setSelectedIds(initialSelectedIds(ticket.produits, prepMap));
  }, [open, ticket?.id, ticket?.produits?.length]);

  const produits = ticket?.produits || [];
  const selectionCount = useMemo(
    () => countSelectedPendingKitchenLines(produits, prepByCommandeProduitId, selectedIds),
    [produits, prepByCommandeProduitId, selectedIds]
  );
  const allPendingCount = useMemo(
    () => countAllPendingKitchenLines(produits, prepByCommandeProduitId),
    [produits, prepByCommandeProduitId]
  );

  const persistSelectionIntents = async () => {
    if (!ticket?.id || ticket.posRevision == null) return null;
    const intents = buildPrepIntentsFromSelection(produits, prepByCommandeProduitId, selectedIds);
    if (!intents.length) return ticket.posRevision;
    const res = await apiService.patchRestaurantPrepReleaseIntents(ticket.id, {
      expectedPosRevision: Number(ticket.posRevision),
      intents,
      commercantId,
    });
    return res?.posRevision ?? Number(ticket.posRevision) + 1;
  };

  const toggleLineSelection = async (lineId) => {
    const line = produits.find((p) => p.id === lineId);
    const prep = prepByCommandeProduitId.get(lineId);
    if (!line || !ticketLineNeedsKitchenSend(line, prep)) return;
    const next = new Set(selectedIds);
    if (next.has(lineId)) next.delete(lineId);
    else next.add(lineId);
    selectionTouchedRef.current = true;
    setSelectedIds(next);
    if (ticket?.posRevision == null) return;
    setIntentSyncing(true);
    try {
      const intents = buildPrepIntentsFromSelection(produits, prepByCommandeProduitId, next);
      const res = await apiService.patchRestaurantPrepReleaseIntents(ticket.id, {
        expectedPosRevision: Number(ticket.posRevision),
        intents,
        commercantId,
      });
      if (res?.posRevision != null) {
        setTicket((prev) => {
          if (!prev) return prev;
          const intents = buildPrepIntentsFromSelection(produits, prepByCommandeProduitId, next);
          const intentById = new Map(intents.map((i) => [i.commandeProduitId, i.prepReleaseIntent]));
          return {
            ...prev,
            posRevision: res.posRevision,
            produits: (prev.produits || []).map((p) =>
              intentById.has(p.id)
                ? { ...p, prepReleaseIntent: intentById.get(p.id) }
                : p
            ),
          };
        });
      }
    } catch (e) {
      setError(e.message || 'Synchronisation impossible');
      await reload();
    } finally {
      setIntentSyncing(false);
    }
  };

  const runSend = async (sendMode) => {
    if (!ticket?.id || sending) return;
    setSending(true);
    setError('');
    setToast('');
    try {
      await persistSelectionIntents();
      const detail = await apiService.getTicketDetail(ticket.id, commercantId);
      const fresh = detail?.ticket;
      if (fresh) setTicket(fresh);

      let commandeProduitIds = null;
      if (sendMode === 'SELECTION') {
        const freshPrepMap = prepMapFromTicketProduits(fresh?.produits || []);
        for (const pl of prepLines) {
          if (pl.commandeProduitId) freshPrepMap.set(pl.commandeProduitId, pl);
        }
        commandeProduitIds = commandeProduitIdsForSelectionSend(
          fresh?.produits || produits,
          freshPrepMap
        );
        if (!commandeProduitIds.length) {
          setError('Sélectionnez au moins un produit à envoyer');
          return;
        }
      }

      const idempotencyKey = createCaisseIdempotencyKey(`rest-prep-detail-${ticket.id}`);
      const outcome = await apiService.sendKitchenCommandePrep(ticket.id, {
        idempotencyKey,
        commercantId,
        sendMode,
        commandeProduitIds,
      });
      const msg = formatPrepSendSuccessToast(outcome);
      if (msg) setToast(msg);
      else setError('Aucune ligne envoyée — vérifiez la réponse serveur');
      selectionTouchedRef.current = false;
      await reload();
      onPrepSent?.();
    } catch (e) {
      if (e?.code === 'PREP_NOTHING_TO_SEND') {
        setError(formatPrepNothingToSendToast(e));
      } else {
        setError(e.message || 'Envoi cuisine impossible');
      }
    } finally {
      setSending(false);
    }
  };

  const markServed = async (line) => {
    if (!line?.id || line.status !== 'READY') return;
    setMarkingId(line.id);
    try {
      await apiService.markRestaurantFloorLineServed(line.id, {
        expectedVersion: line.version,
        commercantId,
      });
      await reload();
      onPrepSent?.();
    } catch (e) {
      setError(e.message || 'Marquage servi impossible');
    } finally {
      setMarkingId(null);
    }
  };

  const requestClose = () => {
    if (sending || intentSyncing) return;
    if (selectionTouchedRef.current) {
      const ok = window.confirm(
        'Des lignes sont sélectionnées pour envoi ou modifiées. Fermer sans enregistrer ?'
      );
      if (!ok) return;
    }
    onClose?.();
  };

  if (!open) return null;

  const encaisse =
    ticket?.montantEncaisse != null && ticket.montantEncaisse !== ''
      ? Number(ticket.montantEncaisse)
      : 0;
  const solde =
    ticket?.soldeRestant != null
      ? Number(ticket.soldeRestant)
      : Number(ticket?.total || 0) - encaisse;
  const payLabel = ticket ? paymentStatusLabel(ticket, solde) : '';

  return (
    <PosModalShell
      open={open}
      onRequestClose={requestClose}
      dismissOnBackdrop={!sending && !intentSyncing}
      allowEscape={!sending && !intentSyncing}
      panelTestId="restaurant-addition-detail-modal"
      aria-labelledby="restaurant-addition-detail-title"
    >
        <div className="shrink-0 border-b bg-white px-4 py-3 flex items-center justify-between gap-2">
          <h2 id="restaurant-addition-detail-title" className="text-lg font-semibold">
            Détail commande
          </h2>
          <button
            type="button"
            className="min-h-[44px] min-w-[44px] rounded-xl border flex items-center justify-center"
            aria-label="Fermer"
            data-testid="restaurant-addition-detail-close"
            onClick={requestClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 space-y-4">
          {loading && <p className="text-sm text-slate-500">Chargement…</p>}
          {error && (
            <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-3">{error}</p>
          )}
          {toast && (
            <p className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-xl p-3">{toast}</p>
          )}
          {ticket && (
            <>
              <div className="text-sm space-y-2 rounded-xl border bg-slate-50 p-3">
                <p>
                  <span className="font-mono font-medium">
                    {ticket.numeroTicket || ticket.numero || ticket.id}
                  </span>
                </p>
                {(ticket.tableLabel ||
                  ticket.posServiceSession?.table?.label ||
                  ticket.restaurantTableLabel) && (
                  <p className="text-slate-700" data-testid="restaurant-addition-detail-table">
                    Table :{' '}
                    {ticket.tableLabel ||
                      ticket.posServiceSession?.table?.label ||
                      ticket.restaurantTableLabel}
                  </p>
                )}
                <p className="font-medium text-slate-900" data-testid="restaurant-addition-payment-status">
                  {payLabel}
                </p>
                <p className="text-slate-700">
                  Total {formatMoney(ticket.total)} · Encaissé {formatMoney(encaisse)} · Solde{' '}
                  {formatMoney(solde)}
                </p>
                {showPrepTracking && allPendingCount > 0 && (
                  <p className="text-xs text-slate-600">
                    Envoi par ligne entière — quantité partielle sur une même ligne non supportée.
                  </p>
                )}
              </div>

              <ul className="space-y-3" data-testid="restaurant-addition-detail-lines">
                {produits.map((l) => {
                  const prep = prepByCommandeProduitId.get(l.id);
                  const pending = ticketLineNeedsKitchenSend(l, prep);
                  const sent = ticketLinePrepActive(prep);
                  const selected = pending && selectedIds.has(l.id);
                  const opLabel = operationalLabel(l, prep, selected);
                  const stationLabel = prep?.station?.label || null;
                  const sentAt = formatTime(prep?.sentAt);
                  const interactive = pending && !sent;
                  return (
                    <li
                      key={l.id}
                      role={interactive ? 'button' : undefined}
                      tabIndex={interactive ? 0 : undefined}
                      data-testid={`restaurant-addition-prep-line-${l.id}`}
                      onClick={interactive ? () => toggleLineSelection(l.id) : undefined}
                      onKeyDown={
                        interactive
                          ? (ev) => {
                              if (ev.key === 'Enter' || ev.key === ' ') {
                                ev.preventDefault();
                                toggleLineSelection(l.id);
                              }
                            }
                          : undefined
                      }
                      className={`border rounded-xl p-3 text-sm space-y-2 transition-colors ${
                        sent
                          ? 'bg-slate-50 border-slate-200'
                          : selected
                            ? 'bg-emerald-50 border-emerald-300 ring-1 ring-emerald-200 cursor-pointer'
                            : pending
                              ? 'bg-white border-dashed border-slate-300 cursor-pointer'
                              : 'border-slate-200'
                      }`}
                    >
                      <div className="flex justify-between gap-2 font-medium">
                        <span>
                          {l.quantite}× {l.nom || l.produit?.nom || 'Produit'}
                        </span>
                        <span>{formatMoney(l.prixTotal ?? l.prixUnitaire * l.quantite)}</span>
                      </div>
                      {lineOptionsText(l) && (
                        <p className="text-slate-600 text-xs">Options : {lineOptionsText(l)}</p>
                      )}
                      {l.commentaire && (
                        <p className="text-slate-600 text-xs">Note : {l.commentaire}</p>
                      )}
                      {showPrepTracking && (
                        <div
                          className="rounded-lg bg-orange-50 border border-orange-100 px-3 py-2 text-xs space-y-1"
                          data-testid={`restaurant-addition-line-prep-${l.id}`}
                        >
                          <p>
                            <span className="font-semibold text-orange-950">{opLabel}</span>
                            {stationLabel ? (
                              <span className="text-orange-800"> · {stationLabel}</span>
                            ) : null}
                          </p>
                          {sentAt && <p className="text-orange-800/80">Envoyé à {sentAt}</p>}
                          {prep?.status === 'READY' && (
                            <>
                              <p className="text-slate-600">
                                Marquage servi sur la ligne entière (pas de fractionnement quantité).
                              </p>
                              <button
                                type="button"
                                className="min-h-[40px] w-full rounded-lg bg-emerald-700 text-white text-sm font-medium disabled:opacity-50"
                                data-testid={`restaurant-mark-served-${prep.id}`}
                                disabled={markingId === prep.id}
                                onClick={(ev) => {
                                  ev.stopPropagation();
                                  markServed(prep);
                                }}
                              >
                                Marquer servi
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
                {produits.length === 0 && (
                  <li className="text-slate-500 text-sm">Aucune ligne produit.</li>
                )}
              </ul>
            </>
          )}
        </div>

        <div className="shrink-0 border-t bg-white px-4 py-3 flex flex-col gap-2">
          {showPrepTracking && ticket && allPendingCount > 0 && (
            <>
              <button
                type="button"
                disabled={sending || intentSyncing || selectionCount === 0}
                className="min-h-[44px] rounded-xl bg-emerald-600 text-white font-medium text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2"
                data-testid="restaurant-addition-send-selection"
                onClick={() => runSend('SELECTION')}
              >
                <ChefHat className="w-4 h-4" />
                Envoyer la sélection ({selectionCount})
              </button>
              <button
                type="button"
                disabled={sending || intentSyncing || allPendingCount === 0}
                className="min-h-[44px] rounded-xl bg-orange-600 text-white font-medium text-sm disabled:opacity-40 inline-flex items-center justify-center gap-2"
                data-testid="restaurant-addition-send-all"
                onClick={() => runSend('ALL_REMAINING')}
              >
                <ChefHat className="w-4 h-4" />
                Tout envoyer ({allPendingCount})
              </button>
            </>
          )}
          {onResumeAddition && (
            <button
              type="button"
              className="min-h-[44px] rounded-xl bg-slate-900 text-white font-medium text-sm"
              data-testid="restaurant-addition-resume"
              onClick={onResumeAddition}
            >
              Reprendre l&apos;addition
            </button>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              className="flex-1 min-h-[44px] rounded-xl border text-sm font-medium inline-flex items-center justify-center gap-2"
              data-testid="restaurant-addition-refresh"
              onClick={() => reload()}
              disabled={loading || sending}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
            <button
              type="button"
              className="flex-1 min-h-[44px] rounded-xl border text-sm font-medium"
              onClick={onClose}
            >
              Retour à la table
            </button>
          </div>
        </div>
    </PosModalShell>
  );
}
